import type { TaskStatus } from '@nexelem/graphne-api';
import { getDuration, useDateFormatter } from '@nexelem/utils';
import { useCustomTranslation } from '../../../../../../hooks';
import { formatUnit } from '../../../Kanban/components/KanbanTask';
import { startedStatuses } from '../../../../../../pages/generic/Products/components/TaskDetailDrawer/constants';
import type { GanttShowOnTaskOptions } from '../../../components/SettingsDrawer/useProductionPlanSettings.types';
import { NodePopoverRow } from './NodePopoverRow';

type NodeSinglePopoverContentProps = {
  title: string;
  total: number;
  unit: string;
  externalId?: string;
  productionOrderNumber?: string;
  dueDate?: string;
  startDate: Date;
  endDate: Date;
  status: TaskStatus;
  showOnTask: GanttShowOnTaskOptions;
};

export const NodeSinglePopoverContent = ({
  title,
  total,
  unit,
  externalId,
  productionOrderNumber,
  dueDate,
  startDate,
  endDate,
  status,
  showOnTask,
}: NodeSinglePopoverContentProps) => {
  const { tWrapper: tDrawer } = useCustomTranslation('products.productionPlan.taskDetailsDrawer');
  const formatDateTime = useDateFormatter({ displayWithHours: true });
  const formatDate = useDateFormatter({});

  const hasStarted = startedStatuses.includes(status);

  const showQuantity = showOnTask.progress;
  const showErpOrderNumber = showOnTask.productionOrderExternalNumber && !!externalId;
  const showProductionOrderNumber = showOnTask.productionOrderNumber && !!productionOrderNumber;
  const showDueDate = showOnTask.dueDate && !!dueDate;
  const showDates = showOnTask.time;

  return (
    <div className="flex min-w-65 flex-col">
      <span className="text-t12-b uppercase">{title}</span>
      <ul className="list-inside list-[square] pl-1 text-t12-sb">
        {showQuantity && <NodePopoverRow label={tDrawer('amount')} value={`${formatUnit(total)} ${unit}`} />}
        {showProductionOrderNumber && <NodePopoverRow label={tDrawer('orderId')} value={productionOrderNumber} />}
        {showErpOrderNumber && <NodePopoverRow label={tDrawer('erpNumber')} value={externalId} />}
        {showDueDate && <NodePopoverRow label={tDrawer('due')} value={formatDate(dueDate)} />}
        {showDates && (
          <>
            <NodePopoverRow
              label={hasStarted ? tDrawer('productionStart') : tDrawer('estimatedStart')}
              value={formatDateTime(startDate)}
            />
            <NodePopoverRow
              label={hasStarted ? tDrawer('productionEnd') : tDrawer('estimatedEnd')}
              value={formatDateTime(endDate)}
            />
            <NodePopoverRow
              label={hasStarted ? tDrawer('productionDuration') : tDrawer('productionEstimatedDuration')}
              value={getDuration(startDate, endDate, ['seconds'])}
            />
          </>
        )}
      </ul>
    </div>
  );
};
